import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmtMoney = (n, currency = 'AUD') => new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(n || 0);

export default function PublicProposal() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acceptForm, setAcceptForm] = useState({ name: '', email: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/share/proposal/${token}`)
      .then((r) => setProposal(r.data))
      .catch(() => toast.error('Proposal not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/share/proposal/${token}/accept`, acceptForm);
      const r = await axios.get(`${API}/share/proposal/${token}`);
      setProposal(r.data);
      toast.success('Proposal accepted — thank you!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-slate-500">Loading…</p></div>;
  if (!proposal) return <div className="min-h-screen flex items-center justify-center"><p className="text-slate-500">Proposal not available.</p></div>;

  const isFinal = ['accepted', 'declined'].includes(proposal.status);

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {proposal.cover_image_url && <img src={proposal.cover_image_url} alt="" className="w-full h-64 object-cover rounded-lg mb-6" />}
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-8 h-8 text-emerald-600" />
          <div>
            <p className="text-xs text-slate-500">Proposal · {proposal.number}</p>
            <h1 className="text-3xl font-bold text-slate-900">{proposal.title}</h1>
            {proposal.client && <p className="text-sm text-slate-600 mt-1">For {proposal.client.name}</p>}
          </div>
        </div>

        <Card className="glass-card p-8 mb-4">
          {proposal.body_html && <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: proposal.body_html }} />}
        </Card>

        {proposal.quote && (
          <Card className="glass-card p-6 mb-4">
            <h2 className="font-semibold mb-3">Investment</h2>
            <table className="w-full text-sm">
              <tbody>
                {(proposal.quote.items || []).map((it) => (
                  <tr key={it.id} className="border-b last:border-0">
                    <td className="py-2">{it.description}</td>
                    <td className="py-2 text-right text-slate-500">{it.quantity} × {fmtMoney(it.unit_price, proposal.quote.currency)}</td>
                    <td className="py-2 text-right font-medium w-28">{fmtMoney((it.quantity || 0) * (it.unit_price || 0), proposal.quote.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end mt-3 pt-3 border-t text-lg"><span className="font-semibold mr-8">Total:</span><span className="font-bold">{fmtMoney(proposal.quote.total, proposal.quote.currency)}</span></div>
          </Card>
        )}

        {(proposal.attachments || []).length > 0 && (
          <Card className="glass-card p-4 mb-4">
            <h3 className="font-semibold mb-2 text-sm">Attachments</h3>
            <ul className="text-sm space-y-1">{proposal.attachments.map((a, i) => <li key={i}><a href={a.url} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">{a.name}</a></li>)}</ul>
          </Card>
        )}

        {isFinal ? (
          <Card className="glass-card p-6 text-center">
            {proposal.status === 'accepted' && <p className="text-emerald-700 font-medium flex items-center justify-center gap-2"><CheckCircle2 className="w-5 h-5" /> Accepted on {proposal.accepted_at?.slice(0, 10)}</p>}
          </Card>
        ) : (
          <Card className="glass-card p-6">
            <h2 className="font-semibold mb-3">Accept this proposal</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div><Label>Name</Label><Input value={acceptForm.name} onChange={(e) => setAcceptForm({ ...acceptForm, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={acceptForm.email} onChange={(e) => setAcceptForm({ ...acceptForm, email: e.target.value })} /></div>
            </div>
            <div className="flex justify-end"><Button onClick={accept} disabled={busy}>{busy ? '…' : 'Accept proposal'}</Button></div>
          </Card>
        )}
      </div>
    </div>
  );
}
